import * as React from 'react';

import { useSidePanel } from '../../providers/SidePanelProvider';

/**
 * Issue the expand for an alert another surface handed to the assistant, once
 * this pane is the one on screen. The receiving half of
 * {@link useOpenAlertInAssistant}.
 *
 * **Deliberately ungated on connection state.** `expandAlert` already owns that
 * policy end to end: a closed or pre-handshake socket queues the frame and
 * dials, a turn in flight queues behind it, and a superseded tab holds it until
 * the user reclaims. A second gate here would be a divergent copy of the same
 * queue, stranding the click in exactly the states that queue exists for.
 *
 * @param openAlert The pane's own opener — `expandAlert` plus whatever the pane
 *   records about the click; see `AssistantPane`.
 */
export function useDrainPendingAlertExpand(openAlert: (alertId: string, wasUnread: boolean) => void): void {
  const { pendingAlertExpand, consumePendingExpand } = useSidePanel();
  // The consume is a state update, so it has not landed by the time StrictMode
  // re-runs this effect with the same render's values — without the ref, a
  // mount holding a pending alert (every below-`xl` click) would expand twice.
  const drainedIdRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (pendingAlertExpand === null) {
      // Re-arms, so clicking the same row again after this one is consumed
      // opens it again rather than reading as already drained.
      drainedIdRef.current = null;
      return;
    }

    const { alertId, wasUnread } = pendingAlertExpand;

    if (drainedIdRef.current === alertId) {
      return;
    }

    drainedIdRef.current = alertId;
    consumePendingExpand();
    openAlert(alertId, wasUnread);
  }, [pendingAlertExpand, consumePendingExpand, openAlert]);
}
